import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faAnchor,
  faArrowTrendUp,
  faBuildingColumns,
  faBullhorn,
  faCircleInfo,
  faCommentDots,
  faCrown,
  faEarthAfrica,
  faGlobe,
  faHandshake,
  faHeart,
  faLocationDot,
  faMapLocationDot,
  faNewspaper,
  faPassport,
  faScaleBalanced,
  faShieldHalved,
  faStore,
  faUsers,
} from '@fortawesome/free-solid-svg-icons';

import AfricaOutline from '@/components/common/AfricaOutline';

/**
 * Font Awesome icons for CMS-driven content.
 *
 * Icon names are stored in the database as Boxicons classes (`bx-world`,
 * `bx-check-shield`, `bx-trending-up`), because that is what the admin has
 * always saved. Rather than rewrite that content — and break any icon an
 * editor picks from the existing Boxicons list — this maps stored names onto
 * Font Awesome equivalents and falls back to a globe for anything unmapped.
 *
 * Add a mapping here when an editor starts using an unmapped Boxicons name;
 * the fallback keeps the page rendering rather than showing an empty tile.
 *
 * Imported per-icon so the bundler tree-shakes: pulling in the whole
 * free-solid set would add hundreds of KB for the handful actually used.
 * Deliberately not the Font Awesome CDN stylesheet — the layout already loads
 * one render-blocking external icon sheet (Boxicons) and a second would be
 * worse for LCP.
 */
const ICON_MAP: Record<string, IconDefinition> = {
  // Geography / diplomacy
  'bx-world': faEarthAfrica,
  'bx-globe': faGlobe,
  'bx-globe-alt': faGlobe,
  'bx-map': faMapLocationDot,
  'bx-map-pin': faLocationDot,
  'bx-current-location': faLocationDot,
  'bx-passport': faPassport,
  'bx-handshake': faHandshake,
  // Trade blocs. Unmapped until now, so the COMESA stat rendered the globe
  // fallback rather than the icon an editor had chosen.
  'bx-store': faStore,

  // Institutions / people
  'bx-bank': faBuildingColumns,
  'bx-building-house': faBuildingColumns,
  'bx-group': faUsers,
  'bx-user': faUsers,
  'bx-balance': faScaleBalanced,
  // "user-voice" is a person with sound waves — a megaphone carries the same
  // sense of being heard better than any person glyph in free-solid.
  'bx-user-voice': faBullhorn,
  'bx-broadcast': faBullhorn,
  'bx-megaphone': faBullhorn,

  // Leadership values
  'bx-check-shield': faShieldHalved,
  'bx-shield': faShieldHalved,
  // Boxicons' own name for the part-filled shield. Unmapped until now, so
  // every card using it fell through to the globe.
  'bx-shield-quarter': faShieldHalved,
  'bx-anchor': faAnchor,
  'bx-trending-up': faArrowTrendUp,
  'bx-heart': faHeart,

  // Editorial
  'bx-message-square-dots': faCommentDots,
  'bx-message': faCommentDots,
  'bx-quote-left': faCommentDots,

  // Section headers
  'bx-crown': faCrown,
  'bx-news': faNewspaper,
  'bx-info-circle': faCircleInfo,
};

/*
 * Names that render a hand-supplied SVG rather than a Font Awesome glyph.
 *
 * Kept in the same lookup as everything else so the choice stays editorial:
 * an editor types the name into the icon field and gets the map, exactly as
 * they would for any other icon.
 */
const CUSTOM_ICONS: Record<string, React.FC<{ className?: string }>> = {
  'bx-africa': AfricaOutline,
  'bx-map-africa': AfricaOutline,
};

function iconKey(name?: string | null): string {
  return (name || '').trim().replace(/^bx\s+/, '');
}

export function resolveCmsIcon(name?: string | null): IconDefinition {
  if (!name) return faEarthAfrica;
  return ICON_MAP[iconKey(name)] || faEarthAfrica;
}

interface CmsIconProps {
  name?: string | null;
  className?: string;
}

const CmsIcon: React.FC<CmsIconProps> = ({ name, className = '' }) => {
  const Custom = CUSTOM_ICONS[iconKey(name)];
  if (Custom) return <Custom data-component-id="src/components/common/CmsIcon.tsx:121:21" data-component-path="src/components/common/CmsIcon.tsx" data-component-line="121" data-component-end-line="121" data-component-file="CmsIcon.tsx" data-component-name="Custom" data-component-content="%7B%22elementName%22%3A%22Custom%22%2C%22className%22%3A%22%5Bvar%3AclassName%5D%22%7D" className={className} />;
  return <FontAwesomeIcon data-component-id="src/components/common/CmsIcon.tsx:122:9" data-component-path="src/components/common/CmsIcon.tsx" data-component-line="122" data-component-end-line="122" data-component-file="CmsIcon.tsx" data-component-name="FontAwesomeIcon" data-component-content="%7B%22elementName%22%3A%22FontAwesomeIcon%22%2C%22className%22%3A%22%5Bvar%3AclassName%5D%22%7D" icon={resolveCmsIcon(name)} className={className} aria-hidden="true" />;
};

export default CmsIcon;
